import {WriteButton, WriteIconButton} from '../../common/buttons/WriteActions';
import {DeleteOutline, EditOutlined} from '@mui/icons-material';
import {Alert, Box, Button, Card, CardActions, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography} from '@mui/material';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {ProductTypeGuidDto} from '../../../models/ProductTypes/IProductTypeGuid';
import {productTypesAPI} from '../../../services/ProductTypeServices';
import {recipeAPI} from '../../../services/RecipeServices';
import {CreateProductTypeDialog} from './CreateProductTypeDialog';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {getApiErrorMessage, getApiFieldErrors} from '../../../common/apiErrors';
import {ContentSkeleton} from '../../common/loading/ContentSkeleton';
import {toSaveProductTypeGuidRequest} from '../../../mappers/productTypeGuid.mapper';

export const ProductTypesPage = () => {
    const {t} = useAppTranslation();
    const navigate = useNavigate();
    const {data: productTypes, error: loadError, isLoading: areTypesLoading, refetch} = productTypesAPI.useGetProductTypesV2Query();
    const {data: recipes, isLoading: areRecipesLoading} = recipeAPI.useGetRecipesV2AllLightQuery();
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [editingType, setEditingType] = useState<ProductTypeGuidDto | null>(null);
    const [name, setName] = useState('');
    const [requestError, setRequestError] = useState<unknown>();
    const [deletingType, setDeletingType] = useState<ProductTypeGuidDto | null>(null);
    const [updateProductType, {isLoading: isUpdating}] = productTypesAPI.useUpdateProductTypeV2Mutation();
    const [deleteProductType, {isLoading: isDeleting}] = productTypesAPI.useDeleteProductTypeV2Mutation();
    const isSaving = isUpdating || isDeleting;

    const openRename = (productType: ProductTypeGuidDto) => {
        setEditingType(productType);
        setName(productType.name);
    };

    const rename = async () => {
        if (!editingType || !name.trim()) return;
        try {
            const updated = {...editingType, name: name.trim()};
            await updateProductType({id: editingType.id, body: toSaveProductTypeGuidRequest(updated, true)}).unwrap();
            setEditingType(null);
        } catch (error) { setRequestError(error); }
    };

    const remove = async () => {
        if (!deletingType) return;
        try { await deleteProductType(deletingType.id).unwrap();
        setDeletingType(null);
        } catch (error) { setRequestError(error); }
    };

    if (areTypesLoading || areRecipesLoading) return <ContentSkeleton variant="list"/>;
    if (loadError) return <Alert severity="error" action={<Button onClick={() => refetch()}>{t('home:retry')}</Button>}>{getApiErrorMessage(loadError, t)}</Alert>;

    const assignedRecipeIds = new Set((productTypes ?? []).flatMap(productType => productType.recipes.map(recipe => recipe.id)));
    const uncategorizedCount = (recipes ?? []).filter(recipe => !assignedRecipeIds.has(recipe.id)).length;

    return (
        <Box sx={{width: '100%'}}>
            <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems={{sm: 'center'}} gap={2} mb={3}>
                <Box>
                    <Typography variant="h4">{t('recipes:product_types')}</Typography>
                    <Typography color="text.secondary">{t('recipes:product_types_description')}</Typography>
                </Box>
                <WriteButton variant="contained" onClick={() => setIsCreateOpen(true)}>+ {t('recipes:add_product_type')}</WriteButton>
            </Stack>

            {!productTypes?.length ? (
                <Stack spacing={1.5}>
                    <Card variant="outlined" sx={{p: 2}}>
                        <Typography variant="h6">{t('recipes:no_product_types')}</Typography>
                        <Typography color="text.secondary" sx={{mb: 2}}>{t('recipes:product_types_empty_description')}</Typography>
                        <WriteButton variant="contained" onClick={() => setIsCreateOpen(true)}>{t('recipes:create_first_product_type')}</WriteButton>
                    </Card>
                    <Card variant="outlined" sx={{bgcolor: 'action.hover'}}>
                        <CardContent><Typography variant="h6">{t('recipes:uncategorized')}</Typography><Typography color="text.secondary">{t('recipes:recipe_count', {count: uncategorizedCount})}</Typography></CardContent>
                    </Card>
                </Stack>
            ) : (
                <Stack spacing={1.5}>
                    {productTypes.map((productType) => (
                        <Card key={productType.id} variant="outlined">
                            <CardContent sx={{display: 'flex', alignItems: 'center', gap: 1, py: '12px !important'}}>
                                <Box sx={{flexGrow: 1, cursor: 'pointer'}} onClick={() => navigate(productType.id)}>
                                    <Typography variant="h6">{productType.name}</Typography>
                                    <Typography color="text.secondary">{t('recipes:recipe_count', {count: productType.recipes?.length || 0})}</Typography>
                                </Box>
                                <WriteIconButton aria-label={t('recipes:rename_product_type')} onClick={() => openRename(productType)}><EditOutlined/></WriteIconButton>
                                <WriteIconButton aria-label={t('common:delete')} color="error" onClick={() => setDeletingType(productType)}><DeleteOutline/></WriteIconButton>
                            </CardContent>
                            <CardActions sx={{pt: 0}}>
                                <Button onClick={() => navigate(productType.id)}>{t('recipes:manage_recipes')}</Button>
                            </CardActions>
                        </Card>
                    ))}
                    <Card variant="outlined" sx={{bgcolor: 'action.hover'}}>
                        <CardContent>
                            <Typography variant="h6">{t('recipes:uncategorized')}</Typography>
                            <Typography color="text.secondary">{t('recipes:recipe_count', {count: uncategorizedCount})}</Typography>
                        </CardContent>
                    </Card>
                </Stack>
            )}

            <CreateProductTypeDialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)}/>
            <Dialog open={Boolean(editingType)} onClose={() => setEditingType(null)} fullWidth maxWidth="xs">
                <DialogTitle>{t('recipes:rename_product_type')}</DialogTitle>
                <DialogContent>{Boolean(requestError) && !getApiFieldErrors(requestError, t).name && <Alert severity="error">{getApiErrorMessage(requestError, t)}</Alert>}<TextField autoFocus fullWidth margin="dense" label={t('recipes:name')} value={name} onChange={(event) => { setName(event.target.value); setRequestError(undefined); }} error={!!getApiFieldErrors(requestError, t).name} helperText={getApiFieldErrors(requestError, t).name}/></DialogContent>
                <DialogActions><Button onClick={() => setEditingType(null)}>{t('common:cancel')}</Button><WriteButton variant="contained" disabled={!name.trim() || isSaving} onClick={() => void rename()}>{t('common:save')}</WriteButton></DialogActions>
            </Dialog>
            <Dialog open={Boolean(deletingType)} onClose={() => setDeletingType(null)} fullWidth maxWidth="xs">
                <DialogTitle>{t('recipes:delete_product_type_confirmation', {name: deletingType?.name})}</DialogTitle>
                <DialogContent>{t('recipes:delete_product_type_description')}{Boolean(requestError) && <Alert severity="error">{getApiErrorMessage(requestError, t)}</Alert>}</DialogContent>
                <DialogActions><Button onClick={() => setDeletingType(null)}>{t('common:cancel')}</Button><WriteButton color="error" variant="contained" disabled={isSaving} onClick={() => void remove()}>{t('common:delete')}</WriteButton></DialogActions>
            </Dialog>
        </Box>
    );
};
